/**
 * `/coffee/accounts` — the manufacturer accounts the machines come from, in
 * the Settings section, behind `coffee.manage`. Add, rename, give new
 * credentials, read the machines again, remove.
 *
 * When the server has no `SECRETS_KEY`, the page says so and offers no way to
 * type a password it could not store.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import CoffeeMakerOutlinedIcon from '@mui/icons-material/CoffeeMakerOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import type { CoffeeAccountDto, CoffeeAccountResponse } from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EntityTable } from '../../components/EntityTable';
import { ErrorState } from '../../components/ErrorState';
import { PageHeader } from '../../components/PageHeader';
import { Timestamp } from '../../components/Timestamp';
import { useAsync } from '../../hooks/useAsync';
import { useSubmit } from '../../hooks/useSubmit';
import { AccountDialog } from './AccountDialog';
import { deleteAccount, listAccounts, listProviders, syncAccount } from './api';

export default function AccountsPage() {
  const { t } = useTranslation(['coffee', 'common']);
  const accounts = useAsync(listAccounts, []);
  const providers = useAsync(listProviders, []);

  /** Undefined: closed. Null: adding. */
  const [editing, setEditing] = useState<CoffeeAccountDto | null | undefined>(undefined);
  const [removing, setRemoving] = useState<CoffeeAccountDto | null>(null);
  const [synced, setSynced] = useState<CoffeeAccountResponse | null>(null);

  const sync = useSubmit(async (id: number) => {
    const result = await syncAccount(id);
    setSynced(result);
    accounts.reload();
    return result;
  });

  const remove = useSubmit(async (id: number) => {
    await deleteAccount(id);
    setRemoving(null);
    accounts.reload();
    return true;
  });

  const canSave = providers.data?.secretsAvailable === true;
  const providerLabel = (key: string) => providers.data?.providers.find((p) => p.key === key)?.label ?? key;

  return (
    <>
      <PageHeader
        title={t('accounts.title')}
        description={t('accounts.description')}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} disabled={!canSave} onClick={() => setEditing(null)}>
            {t('accounts.add')}
          </Button>
        }
      />

      {providers.data && !providers.data.secretsAvailable && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('accounts.noSecrets')}
        </Alert>
      )}
      {providers.error && <ErrorState error={providers.error} onRetry={providers.reload} />}
      {sync.error && <ErrorState error={sync.error} severity="warning" />}
      {synced && (
        <Alert severity="success" onClose={() => setSynced(null)} sx={{ mb: 2 }}>
          {t('accounts.synced', { count: synced.machines.length, label: synced.account.label })}
        </Alert>
      )}

      <AsyncContent state={accounts}>
        {({ accounts: rows }) => (
          <EntityTable<CoffeeAccountDto>
            rows={rows}
            rowKey={(row) => row.id}
            empty={{
              icon: <CoffeeMakerOutlinedIcon fontSize="inherit" />,
              title: t('accounts.emptyTitle'),
              description: t('accounts.emptyDescription'),
            }}
            columns={[
              { field: 'label', label: t('accounts.column.label'), render: (row) => row.label },
              { field: 'provider', label: t('accounts.column.provider'), render: (row) => providerLabel(row.provider) },
              { field: 'identity', label: t('accounts.column.identity'), render: (row) => row.identity },
              { field: 'machines', label: t('accounts.column.machines'), align: 'right', width: 110, render: (row) => row.machineCount },
              {
                field: 'synced',
                label: t('accounts.column.synced'),
                width: 150,
                render: (row) => (row.syncedAt ? <Timestamp value={row.syncedAt} /> : t('accounts.never')),
              },
            ]}
            actionsWidth={140}
            actions={(row) => [
              {
                key: 'edit',
                label: t('accounts.edit'),
                icon: <EditOutlinedIcon fontSize="small" />,
                disabled: !canSave,
                onClick: () => setEditing(row),
              },
              {
                key: 'sync',
                label: t('accounts.sync'),
                icon: <SyncIcon fontSize="small" />,
                disabled: sync.pending,
                onClick: () => void sync.submit(row.id),
              },
              {
                key: 'remove',
                label: t('accounts.remove'),
                icon: <DeleteOutlinedIcon fontSize="small" />,
                tone: 'error',
                onClick: () => setRemoving(row),
              },
            ]}
          />
        )}
      </AsyncContent>

      <AccountDialog
        open={editing !== undefined}
        providers={providers.data?.providers ?? []}
        account={editing ?? null}
        onClose={() => setEditing(undefined)}
        onSaved={() => {
          setEditing(undefined);
          accounts.reload();
        }}
      />

      <ConfirmDialog
        open={removing !== null}
        title={t('accounts.removeTitle', { label: removing?.label ?? '' })}
        message={t('accounts.removeMessage')}
        confirmLabel={t('accounts.remove')}
        tone="error"
        pending={remove.pending}
        error={remove.error}
        onConfirm={() => removing && void remove.submit(removing.id)}
        onClose={() => {
          remove.clearError();
          setRemoving(null);
        }}
      />
    </>
  );
}
